import { toast } from "sonner";

interface SupabaseError {
  message?: string;
  code?: string;
  status?: number;
  details?: string;
}

/**
 * Handles Supabase errors and displays user-friendly toast messages
 * @param error - The error object from Supabase
 * @param fallbackMessage - Default message if error type is not recognized
 */
export function handleSupabaseError(error: SupabaseError | any, fallbackMessage = "An error occurred"): void {
  const errorMessage = error?.message?.toLowerCase() || "";
  const errorCode = error?.code || "";
  const status = error?.status;

  // Egress/bandwidth limit errors
  if (
    errorMessage.includes("egress") ||
    errorMessage.includes("bandwidth") ||
    errorMessage.includes("quota") ||
    errorMessage.includes("limit exceeded") ||
    status === 509 ||
    errorCode === "PGRST301"
  ) {
    toast.error("Service temporarily unavailable. Please contact the website admin.", {
      description: "Database bandwidth limit may have been reached.",
    });
    return;
  }

  // Rate limit errors
  if (
    errorMessage.includes("rate limit") ||
    errorMessage.includes("too many requests") ||
    status === 429
  ) {
    toast.error("Too many requests. Please wait a moment and try again.");
    return;
  }

  // RLS/Permission errors
  if (errorCode === "42501" || errorMessage.includes("permission denied")) {
    toast.error("Permission denied. Please ensure you're signed in.");
    return;
  }

  // Unique constraint violations
  if (errorCode === "23505" || errorMessage.includes("duplicate key")) {
    toast.error("This action has already been performed.");
    return;
  }

  // Connection/timeout errors
  if (
    errorMessage.includes("timeout") ||
    errorMessage.includes("connection") ||
    errorMessage.includes("network") ||
    status === 504
  ) {
    toast.error("Connection error. Please check your internet and try again.");
    return;
  }

  // Service unavailable
  if (status === 503 || errorMessage.includes("service unavailable")) {
    toast.error("Service temporarily unavailable. Please try again later.", {
      description: "The server may be under maintenance.",
    });
    return;
  }

  // Default fallback
  toast.error(fallbackMessage);
}

/**
 * Extracts a user-friendly error message from Supabase error
 */
export function getErrorMessage(error: SupabaseError | any, fallbackMessage = "An error occurred"): string {
  const errorMessage = error?.message?.toLowerCase() || "";
  const errorCode = error?.code || "";
  const status = error?.status;

  if (
    errorMessage.includes("egress") ||
    errorMessage.includes("bandwidth") ||
    errorMessage.includes("quota") ||
    status === 509
  ) {
    return "Service temporarily unavailable. Database bandwidth limit may have been reached. Please contact the website admin.";
  }

  if (errorMessage.includes("rate limit") || status === 429) {
    return "Too many requests. Please wait a moment and try again.";
  }

  if (errorCode === "42501") {
    return "Permission denied. Please ensure you're signed in.";
  }

  if (errorCode === "23505") {
    return "This action has already been performed.";
  }

  return fallbackMessage;
}
